<script setup>

import {computed, onMounted, reactive, ref} from "vue";
import {useAdminStore} from "@/store/admin";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import FormInput from "@/components/ui/FormInput.vue";
import CheckBox from "@/components/ui/CheckBox.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import TablePager from "@/components/shared/TablePager.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";
import FileButton from "@/components/ui/FileButton.vue";
import {openImagePreview} from "@/composables/image-preview";

const adminStore = useAdminStore();
const toast = useToast();

const items = computed(() => adminStore.products);

function categoryName(categoryId) {
    return adminStore.categories.find(category => category.id === categoryId)?.nm || '—';
}

const PRODUCTS_PER_PAGE_STORAGE_KEY = 'adminProductsPerPage';
const currentPage = ref(0);
const PER_PAGE = ref(parseInt(localStorage.getItem(PRODUCTS_PER_PAGE_STORAGE_KEY)) || 50);

const countPages = computed(() => Math.max(1, Math.ceil((adminStore.totalProducts || 0) / PER_PAGE.value)));
const hasMoreData = computed(() => (adminStore.totalProducts || 0) > (currentPage.value + 1) * PER_PAGE.value);

async function loadProducts() {
    await adminStore.loadProductsData({page: currentPage.value + 1, per_page: PER_PAGE.value});
}

async function setPage(p) {
    if (p < 0 || p >= countPages.value) return;
    currentPage.value = p;
    await loadProducts();
}

function setPerPage(n) {
    const val = parseInt(n);
    if (!val || val < 1) return;
    PER_PAGE.value = val;
    localStorage.setItem(PRODUCTS_PER_PAGE_STORAGE_KEY, String(val));
    currentPage.value = 0;
    loadProducts();
}

const pagination = {
    loading: computed(() => adminStore.loading),
    currentPage,
    countPages,
    hasMoreData,
    perPage: PER_PAGE,
    setPage,
    setPerPage,
    firstPage: () => setPage(0),
    decPage: () => setPage(currentPage.value - 1),
    incPage: () => setPage(currentPage.value + 1),
    lastPage: () => setPage(countPages.value - 1),
};

const editing = ref(null);
const showForm = ref(false);
const form = reactive(defaultForm());
const imageSlots = ['img', 'img1', 'img2', 'img3', 'img4'];
const selectedImages = reactive({img: null, img1: null, img2: null, img3: null, img4: null});
const imagePreviews = reactive({img: '', img1: '', img2: '', img3: '', img4: ''});

function selectImage(slot, file) {
    selectedImages[slot] = file || null;
    if (imagePreviews[slot]) URL.revokeObjectURL(imagePreviews[slot]);
    imagePreviews[slot] = selectedImages[slot] ? URL.createObjectURL(selectedImages[slot]) : '';
}

function defaultForm() {
    return {
        nm: "",
        brand: "",
        oem: "",
        description: "",
        comment: "",
        delivery_time: "",
        cost: 0,
        price: 0,
        author: "",
        supplier: "",
        batch_number: "",
        category_id: "",
        qnt: 0,
        weight: 0,
        production_date: "",
        supports_delivery: false,
    };
}

function resetForm() {
    Object.assign(form, defaultForm());
    for (const slot of imageSlots) {
        selectedImages[slot] = null;
        if (imagePreviews[slot]) URL.revokeObjectURL(imagePreviews[slot]);
        imagePreviews[slot] = '';
    }
}

function openCreate() {
    editing.value = {};
    showForm.value = true;
    resetForm();
}

function openEdit(item) {
    editing.value = item;
    showForm.value = true;
    Object.assign(form, {
        nm: item.nm || "",
        brand: item.brand || "",
        oem: item.oem || "",
        description: item.description || "",
        comment: item.comment || "",
        delivery_time: item.delivery_time || "",
        cost: item.cost ?? 0,
        price: item.price ?? 0,
        author: item.author || "",
        supplier: item.supplier || "",
        batch_number: item.batch_number || "",
        category_id: item.category_id || "",
        qnt: item.qnt ?? 0,
        weight: item.weight ?? 0,
        production_date: item.production_date || "",
        supports_delivery: !!item.supports_delivery,
    });
}

function cancelForm() {
    editing.value = null;
    showForm.value = false;
    resetForm();
}

async function saveHandler() {
    const payload = {
        nm: form.nm,
        brand: form.brand,
        oem: form.oem,
        description: form.description,
        comment: form.comment,
        delivery_time: form.delivery_time,
        cost: Number(form.cost),
        price: Number(form.price),
        author: form.author,
        supplier: form.supplier,
        batch_number: form.batch_number,
        category_id: form.category_id,
        qnt: Number(form.qnt),
        weight: Number(form.weight),
        production_date: form.production_date || null,
        supports_delivery: form.supports_delivery,
    };
    const isEdit = !!editing.value?.id;
    const ok = isEdit
        ? await adminStore.updateItem('products', editing.value.id, payload)
        : await adminStore.createItem('products', payload);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        const product = isEdit ? editing.value : ok;
        if (!isEdit) editing.value = product;
        for (const slot of imageSlots) {
            if (!selectedImages[slot]) continue;
            const uploaded = await adminStore.uploadProductImage(product.id, slot, selectedImages[slot]);
            if (!uploaded) {
                toast.error(adminStore.error);
                await loadProducts();
                return;
            }
        }
        toast.success(isEdit ? "Товар обновлён" : "Товар создан");
        cancelForm();
        await loadProducts();
    }
}

async function deleteHandler(item) {
    if (!confirm(`Удалить товар "${item.nm || item.id}"?`)) return;
    const ok = await adminStore.deleteItem('products', item.id);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success("Товар удалён");
        await loadProducts();
    }
}

onMounted(() => {
    adminStore.loadCategories();
    loadProducts();
});

</script>

<template>
  <div class="adminCrud">
    <MainPreloader
        v-if="adminStore.loading"
        position="fixed"
        :size="80"
        :stroke="3"
    />

    <div class="adminCrud__toolbar">
      <div class="adminCrud__toolbarTitle">Товары</div>
      <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="openCreate">
        <template #caption>Добавить товар</template>
      </ActionButton>
    </div>

    <AdminModal :visible="showForm" :title="editing?.id ? 'Редактирование товара' : 'Новый товар'" @close="cancelForm">
      <div class="adminCrud__formGrid">
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Название</label>
          <form-input v-model="form.nm"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Бренд</label>
          <form-input v-model="form.brand"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">OEM</label>
          <form-input v-model="form.oem"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Категория</label>
          <select v-model="form.category_id" class="adminCrud__select">
            <option value="">— Без категории —</option>
            <option v-for="cat in adminStore.categories" :key="cat.id" :value="cat.id">
              {{ cat.nm }}
            </option>
          </select>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Срок доставки</label>
          <form-input v-model="form.delivery_time"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Себестоимость</label>
          <form-input v-model="form.cost"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Цена</label>
          <form-input v-model="form.price"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Количество</label>
          <form-input v-model="form.qnt"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Вес</label>
          <form-input v-model="form.weight"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Автор</label>
          <form-input v-model="form.author"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Поставщик</label>
          <form-input v-model="form.supplier"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Номер партии</label>
          <form-input v-model="form.batch_number"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Дата производства</label>
          <form-input v-model="form.production_date"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Описание</label>
          <form-input v-model="form.description" rows="3"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Комментарий</label>
          <form-input v-model="form.comment" rows="3"/>
        </div>
        <div v-for="(slot, index) in imageSlots" :key="slot" class="adminCrud__field">
          <label class="adminCrud__label">Изображение {{ index + 1 }} (JPEG, PNG или GIF)</label>
          <FileButton accept="image/jpeg,image/png,image/gif" @select="(file) => selectImage(slot, file)"/>
          <img
              v-if="imagePreviews[slot]"
              :src="imagePreviews[slot]"
              alt="Предпросмотр"
              class="adminCrud__imagePreview"
              @click="openImagePreview(imagePreviews[slot])"
          />
          <img
              v-else-if="editing?.[slot === 'img' ? 'img_url' : `img_url${slot.slice(3)}`]"
              :src="editing[slot === 'img' ? 'img_url' : `img_url${slot.slice(3)}`]"
              alt="Текущее изображение"
              class="adminCrud__imagePreview"
              @click="openImagePreview(editing[slot === 'img' ? 'img_url' : `img_url${slot.slice(3)}`])"
          />
        </div>
      </div>
      <div class="adminCrud__checks">
        <check-box v-model="form.supports_delivery" text="Доставка поддерживается"/>
      </div>
      <template #footer>
        <ActionButton @buttonClick="cancelForm">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="saveHandler">
          <template #caption>Сохранить</template>
        </ActionButton>
      </template>
    </AdminModal>

    <EmptyCard v-if="!items.length && !adminStore.loading"/>

    <TablePager
        v-if="items.length && pagination.countPages.value >= 1"
        :pagination="pagination"
    />

    <table v-if="items.length" class="adminTable">
      <thead>
      <tr>
        <th></th>
        <th>Название</th>
        <th>Категория</th>
        <th>Цена</th>
        <th>Кол-во</th>
        <th>Доставка</th>
        <th>Действия</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in items" :key="item.id">
        <td>
          <img
              v-if="item.img_url"
              :src="item.img_url"
              alt=""
              class="adminCrud__thumb"
              @click="openImagePreview(item.img_url)"
          />
          <span v-else class="adminCrud__thumb adminCrud__thumb--empty"></span>
        </td>
        <td>{{ item.nm || '—' }}</td>
        <td>{{ categoryName(item.category_id) }}</td>
        <td>{{ item.price ?? '—' }}</td>
        <td>{{ item.qnt ?? '—' }}</td>
        <td>{{ item.supports_delivery ? 'Да' : 'Нет' }}</td>
        <td class="adminCrud__actions">
          <button class="adminCrud__actionButton" title="Изменить" @click="openEdit(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="adminCrud__actionButton adminCrud__actionButton--danger" title="Удалить" @click="deleteHandler(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </td>
      </tr>
      </tbody>
    </table>

    <TablePager
        v-if="items.length && pagination.countPages.value >= 1"
        :pagination="pagination"
    />
  </div>
</template>

<style scoped lang="scss">
.adminCrud {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__toolbarTitle {
    color: var(--cd-base-green);
    font-size: 16px;
    font-family: var(--cd-default-font);
  }

  &__form {
    background: var(--cd-card-bg);
    border-radius: 20px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: left;
  }

  &__formTitle {
    color: var(--cd-base-green);
    font-size: 16px;
    font-family: var(--cd-default-font);
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: 1 / -1;
    }
  }

  &__label {
    color: var(--cd-gray-alt);
    font-size: 13px;
  }

  &__imagePreview {
    max-width: 180px;
    max-height: 140px;
    object-fit: contain;
    cursor: zoom-in;
  }

  &__thumb {
    display: block;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    background: var(--cd-bg-dark);
    cursor: zoom-in;

    &--empty {
      background: transparent;
      border: 1px solid var(--cd-border);
      cursor: default;
    }
  }

  &__select {
    width: 100%;
    height: 48px;
    border-radius: 20px;
    padding: 5px 20px;
    background: var(--cd-field-bg);
    color: var(--cd-field-color);
    border: 1px solid var(--cd-field-border);
    font-size: 14px;
    font-family: var(--cd-default-font);

    &:focus {
      outline: none;
      border-color: var(--cd-base-green);
      box-shadow: 0 0 5px rgba(98, 192, 100, 0.3);
    }
  }

  &__checks {
    display: flex;
    gap: 24px;
  }

  &__formActions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminCrud__actionButton + .adminCrud__actionButton {
      margin-left: 8px;
    }
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &--danger {
      border-color: var(--cd-danger);
      color: var(--cd-danger);

      &:hover {
        background: var(--cd-danger);
        color: #ffffff;
      }
    }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child {
      border-radius: 12px 0 0 12px;
    }

    &:last-child {
      border-radius: 0 12px 12px 0;
    }
  }
}
</style>
